// Feature-chat failure classification (ORP-20465). Pure: branches on `status`/`code`/headers only,
// NEVER on message text (contract 03 §5) — the backend's 429 copy is prose and its wording is not
// a contract.
import type { FeatureChatTurnError } from '../models/featureChat.types'

import { isCreditDepletedError } from '@/features/credits'
import { ApiError } from '@/shared/http'

/**
 * The backend's user-visible message, when it sent one.
 *
 * Feature chat answers with `{ success: false, error: "…" }` — a bare string under `error`, not the
 * canonical `{ error: { message } }` envelope — which `ApiError.detail` already flattens. Credit
 * depletion is the one path that DOES use the envelope, and it is classified before this runs.
 */
function backendMessage(error: ApiError): string | null {
  const detail = error.detail?.trim()
  return detail ? detail : null
}

/**
 * Map a failed ask onto the surface the panel renders.
 *
 * The three kinds have three different remedies and must stay apart:
 *   • `rateLimited` (429 + `Retry-After`) — wait, then the same question works.
 *   • `creditDepleted` (402 `CREDIT_DEPLETED`) — waiting changes nothing; buy or upgrade.
 *   • `generic` — 400/403/404/500 and transport failures; retry or give up.
 *
 * Depletion is checked first: it is a 402, so no status branch below can shadow it, but the order
 * makes the precedence explicit rather than incidental.
 */
export function classifyFeatureChatError(error: unknown): FeatureChatTurnError {
  if (isCreditDepletedError(error)) {
    return {
      kind: 'creditDepleted',
      retryAfterSeconds: null,
      message: ApiError.isApiError(error) ? backendMessage(error) : null,
    }
  }

  if (!ApiError.isApiError(error)) {
    return { kind: 'generic', retryAfterSeconds: null, message: null }
  }

  if (error.status === 429) {
    // Round UP: a countdown that reaches zero before the window does invites a second 429.
    const ms = error.retryAfterMs
    return {
      kind: 'rateLimited',
      retryAfterSeconds: ms === null ? null : Math.max(1, Math.ceil(ms / 1000)),
      message: backendMessage(error),
    }
  }

  // A cancelled turn (unmount, navigation) is not a failure and carries no backend text.
  if (error.kind === 'canceled') {
    return { kind: 'generic', retryAfterSeconds: null, message: null }
  }

  return {
    kind: 'generic',
    retryAfterSeconds: null,
    message: error.userVisible ? backendMessage(error) : null,
  }
}
